/**
 * ShipmentsTable - Tabela oficial de Remessas (padrão ServiceOrdersTable).
 *
 * Barra lateral na cor da situação, número compacto, situação em 2º,
 * progresso no caminho Rascunho → Entregue, faixa de prazo, duração,
 * destinatário com tipo, rastreio destacado, frete e datas. Ordenação
 * server-side via SortableHeader (allowlist em get_shipments_paginated).
 *
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial (extraída de ShipmentsPage).
 */
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { SortableHeader } from '@/components/layout/list-page';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { DealPhaseProgress } from '@/components/crm/deals/DealPhaseProgress';
import { formatStockDateTime } from '@/lib/stock-presentation';
import { formatShipmentDuration, getShipmentDeadline } from '@/lib/shipmentDeadline';
import { recipientKindLabel } from '@/lib/shipmentLinkPermissions';
import type { ShipmentRow } from '@/hooks/useShipments';
import { ShipmentDeadlineChip } from './ShipmentDeadlineChip';
import { ShipmentTrackingCode } from './ShipmentTrackingCode';
import { SHIPMENT_STATUS_COLOR, SHIPMENT_STATUS_META, shipmentProgress, type ShipmentSortField } from './shipmentStatus';

interface Props {
  items: ShipmentRow[];
  isLoading: boolean;
  sortField: ShipmentSortField;
  sortDirection: 'asc' | 'desc';
  onSort: (field: ShipmentSortField) => void;
  onOpen: (id: string) => void;
}

const fmtDate = (v: string | null) => (v ? format(new Date(`${v.slice(0, 10)}T12:00:00`), 'dd/MM/yyyy') : '—');
const money = (v: number | null | undefined) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

export function ShipmentsTable({ items, isLoading, sortField, sortDirection, onSort, onOpen }: Props) {
  // Relógio de 1 minuto: atualiza prazo e duração em trânsito sem recarregar
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
  const sh = (label: string, field: ShipmentSortField) => (
    <SortableHeader label={label} field={field} currentField={sortField} currentDirection={sortDirection} onSort={onSort} />
  );
  const th = (label: string) => <TableHead className="text-xs font-medium">{label}</TableHead>;

  return (
    <Table className="min-w-[1640px]">
      <TableHeader>
        <TableRow>
          <TableHead className="w-8 px-2" aria-label="Indicador visual" />
          {sh('N. Remessa', 'number')}
          {/* v1.1.0: coluna Situação removida — Progresso já mostra a situação e ordena por ela */}
          {sh('Progresso', 'status')}
          {th('Prazo')}
          {th('Duração')}
          {sh('Destinatário', 'recipient')}
          {sh('Transportadora', 'carrier')}
          {th('Rastreio')}
          {sh('Frete', 'freight')}
          {sh('Previsão', 'expected')}
          {sh('Enviado em', 'shipped_at')}
          {sh('Entregue em', 'delivered_at')}
          {sh('Criada em', 'created_at')}
        </TableRow>
      </TableHeader>
      <TableBody className="[&>tr>td]:py-2.5">
        {isLoading ? Array.from({ length: 5 }).map((_, i) => (
          <TableRow key={i}><TableCell colSpan={14}><Skeleton className="h-10 w-full" /></TableCell></TableRow>
        )) : items.length === 0 ? (
          <TableRow><TableCell colSpan={14} className="text-center text-sm text-muted-foreground"><div className="py-10">Nenhuma remessa encontrada.</div></TableCell></TableRow>
        ) : items.map((r) => {
          const color = SHIPMENT_STATUS_COLOR[r.status];
          const prog = shipmentProgress(r.status);
          return (
            <TableRow key={r.id} className="cursor-pointer" onClick={() => onOpen(r.id)}>
              <TableCell className="px-2"><div className="h-8 w-1 rounded-full" style={{ backgroundColor: color }} title={SHIPMENT_STATUS_META[r.status].label} /></TableCell>
              <TableCell>
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <Badge variant="outline" className="h-5 whitespace-nowrap px-1.5 text-[10px] font-medium tabular-nums">{r.number}</Badge>
                  {r.direction === 'return' && <Badge variant="secondary" className="h-4 px-1 text-[9px]">Retorno</Badge>}
                </div>
              </TableCell>
              <TableCell><DealPhaseProgress phaseName={SHIPMENT_STATUS_META[r.status].label} phaseColor={color} phaseSortOrder={prog.step} totalPhases={prog.total} /></TableCell>
              <TableCell><ShipmentDeadlineChip variant="compact" deadline={getShipmentDeadline(r.expected_delivery_date, r.delivered_at, r.status, now)} /></TableCell>
              <TableCell className="whitespace-nowrap text-xs tabular-nums">{formatShipmentDuration(r.shipped_at, r.delivered_at, now) ?? '—'}</TableCell>
              <TableCell>
                <div className="flex h-10 max-w-[170px] flex-col justify-center overflow-hidden" title={r.city ? `${r.city}${r.state ? `/${r.state}` : ''}` : undefined}>
                  <span className="truncate text-sm font-medium">{r.recipient_name}</span>
                  <span className="truncate text-xs text-muted-foreground">{recipientKindLabel(r.recipient_customer_id, r.recipient_roles)}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex h-10 max-w-[160px] flex-col justify-center overflow-hidden">
                  <span className="truncate text-sm font-medium">{r.carrier_name ?? '—'}</span>
                  {r.carrier_service && <span className="truncate text-xs text-muted-foreground">{r.carrier_service}</span>}
                </div>
              </TableCell>
              <TableCell>{r.tracking_code ? <ShipmentTrackingCode code={r.tracking_code} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
              <TableCell className="whitespace-nowrap text-xs tabular-nums">{money(r.freight_cost)}</TableCell>
              <TableCell className={r.is_overdue ? 'whitespace-nowrap text-xs font-medium text-destructive' : 'whitespace-nowrap text-xs'}>{fmtDate(r.expected_delivery_date)}</TableCell>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{r.shipped_at ? formatStockDateTime(r.shipped_at) : '—'}</TableCell>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{r.delivered_at ? formatStockDateTime(r.delivered_at) : '—'}</TableCell>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatStockDateTime(r.created_at)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
